import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import {
  isIElementGenericDataset,
  isIElementGenericImgSheet,
  isIElementGenericPointCloudStream,
  isIElementImg360,
  isIElementRoomSection,
} from "@faro-lotv/ielement-types";
import { IElementsRecord } from "@faro-lotv/project-source";
import { DataSetAreaInfo } from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useEffect } from "react";

/**
 * Debug hook to log iElements stats to console, whenever the ielements slice is changed
 *
 * @param enable Set to true to enable logging
 */
export function useIElementsStats(enable: boolean): void {
  const stats = useAppSelector(selectIElementsStats(enable), isEqual);
  useEffect(() => {
    if (stats) {
      console.log(stats);
    }
  }, [stats]);
}

const selectIElementsStats = curryAppSelector(
  createSelector(
    [
      (state: RootState) => state.iElements.iElements,
      (state: RootState) => state.iElements.areaDataSets,
      (state: RootState, enable: boolean) => enable,
    ],
    computeIElementsStats,
  ),
);

type Stats = {
  total: number;
  panos: number;
  rooms: number;
  sheets: number;
  datasets: number;
  pointclouds: number;
  areas: number;
};

function computeIElementsStats(
  iElements: IElementsRecord,
  areaDataSets: Record<GUID, DataSetAreaInfo[] | undefined>,
  enable: boolean,
): Stats | undefined {
  if (!enable) return;

  const elements = Object.values(iElements);
  let panos = 0;
  let rooms = 0;
  let sheets = 0;
  let datasets = 0;
  let pointclouds = 0;
  let total = 0;
  for (const el of elements) {
    if (!el) continue;
    total += 1;
    if (isIElementRoomSection(el)) {
      rooms += 1;
    } else if (isIElementImg360(el)) {
      panos += 1;
    } else if (isIElementGenericImgSheet(el)) {
      sheets += 1;
    } else if (isIElementGenericDataset(el)) {
      datasets += 1;
    } else if (isIElementGenericPointCloudStream(el)) {
      pointclouds += 1;
    }
  }
  return {
    total,
    panos,
    rooms,
    sheets,
    datasets,
    pointclouds,
    areas: Object.keys(areaDataSets).length,
  };
}
